import { useState, useEffect, useCallback } from "react";

interface KpiData {
  label: string;
  value: number;
  change_value: string;
  trend: "up" | "down";
  icon: string;
  sub_label: string;
}

interface RevenueRow {
  month: string;
  ca: number;
}

interface TreasuryRow {
  month: string;
  entrees: number;
  sorties: number;
  solde: number;
}

interface ActionRow {
  id: string;
  label: string;
  status: string;
  due_date: string | null;
}

interface DocumentRow {
  id: string;
  name: string;
  type: string;
  doc_date: string | null;
}

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Pas de données de démonstration : un état vide vaut mieux qu'un chiffre inventé.
const DEFAULT_REVENUE: RevenueRow[] = [];

// Pas de données de démonstration : un état vide vaut mieux qu'un chiffre inventé.
const DEFAULT_TREASURY: TreasuryRow[] = [];

// Pas de données de démonstration : un état vide vaut mieux qu'un chiffre inventé.
const DEFAULT_ACTIONS: ActionRow[] = [];

// Pas de données de démonstration : un état vide vaut mieux qu'un chiffre inventé.
const DEFAULT_DOCUMENTS: DocumentRow[] = [];

export function useDashboardData(userId: string | undefined) {
  const [kpis, setKpis] = useState<KpiData[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueRow[]>([]);
  const [treasuryData, setTreasuryData] = useState<TreasuryRow[]>([]);
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);

    // Fetch real KPIs from backend
    try {
      const [kpiRes, notifRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/kpis`, { credentials: "include" }),
        fetch(`${API_URL}/api/dashboard/notifications`, { credentials: "include" }),
      ]);

      if (kpiRes.ok) {
        const kpiData = await kpiRes.json();
        const notifData = notifRes.ok ? await notifRes.json() : { total_users: 0 };

        const realKpis: KpiData[] = [
          {
            label: "Utilisateurs inscrits",
            value: notifData.total_users || 0,
            change_value: notifData.new_registrations_count > 0 ? `+${notifData.new_registrations_count}` : "0",
            trend: notifData.new_registrations_count > 0 ? "up" : "down",
            icon: "Users",
            sub_label: "cette semaine",
          },
          {
            label: "Prospects",
            value: kpiData.total_prospects || 0,
            change_value: kpiData.prospects_this_month > 0 ? `+${kpiData.prospects_this_month}` : "0",
            trend: kpiData.prospects_this_month > 0 ? "up" : "down",
            icon: "UserPlus",
            sub_label: "ce mois",
          },
          {
            label: "Taux de conversion",
            value: kpiData.conversion_rate || 0,
            change_value: `${kpiData.conversion_rate || 0}%`,
            trend: kpiData.conversion_rate > 0 ? "up" : "down",
            icon: "Euro",
            sub_label: "qualifiés → clients",
          },
          {
            label: "Conversations IA",
            value: kpiData.total_conversations || 0,
            change_value: `${kpiData.active_sessions || 0} sessions`,
            trend: "up",
            icon: "Landmark",
            sub_label: "sessions actives (7j)",
          },
        ];
        setKpis(realKpis);
      } else {
        // Fallback if not authenticated
        setKpis([
          { label: "Utilisateurs", value: 0, change_value: "0", trend: "down", icon: "Users", sub_label: "inscrits" },
          { label: "Prospects", value: 0, change_value: "0", trend: "down", icon: "UserPlus", sub_label: "ce mois" },
          { label: "Conversion", value: 0, change_value: "0%", trend: "down", icon: "Euro", sub_label: "taux" },
          { label: "Conversations", value: 0, change_value: "0", trend: "down", icon: "Landmark", sub_label: "sessions" },
        ]);
      }
    } catch {
      setKpis([
        { label: "Utilisateurs", value: 0, change_value: "0", trend: "down", icon: "Users", sub_label: "inscrits" },
        { label: "Prospects", value: 0, change_value: "0", trend: "down", icon: "UserPlus", sub_label: "ce mois" },
        { label: "Conversion", value: 0, change_value: "0%", trend: "down", icon: "Euro", sub_label: "taux" },
        { label: "Conversations", value: 0, change_value: "0", trend: "down", icon: "Landmark", sub_label: "sessions" },
      ]);
    }

    // Fetch Qonto transactions for real treasury data
    try {
      const txRes = await fetch(`${API_URL}/api/qonto/transactions?per_page=30`, { credentials: "include" });
      if (txRes.ok) {
        const txData = await txRes.json();
        const txs = txData.transactions || [];

        // Group transactions by month for charts
        const monthMap: Record<string, { entrees: number; sorties: number }> = {};
        const revenueMap: Record<string, number> = {};
        for (const tx of txs) {
          const d = tx.settled_at || tx.emitted_at;
          if (!d) continue;
          const dt = new Date(d);
          const key = dt.toLocaleDateString("fr-FR", { month: "short" });
          if (!monthMap[key]) monthMap[key] = { entrees: 0, sorties: 0 };
          if (tx.side === "credit") {
            monthMap[key].entrees += tx.amount_cents ? tx.amount_cents / 100 : tx.amount || 0;
            revenueMap[key] = (revenueMap[key] || 0) + (tx.amount_cents ? tx.amount_cents / 100 : tx.amount || 0);
          } else {
            monthMap[key].sorties += tx.amount_cents ? tx.amount_cents / 100 : tx.amount || 0;
          }
        }

        const months = Object.keys(monthMap);
        if (months.length > 0) {
          setTreasuryData(months.map(m => ({
            month: m,
            entrees: Math.round(monthMap[m].entrees),
            sorties: Math.round(monthMap[m].sorties),
            solde: Math.round(monthMap[m].entrees - monthMap[m].sorties),
          })));
          setRevenueData(months.map(m => ({
            month: m,
            ca: Math.round(revenueMap[m] || 0),
          })));
        } else {
          setRevenueData(DEFAULT_REVENUE);
          setTreasuryData(DEFAULT_TREASURY);
        }
      } else {
        setRevenueData(DEFAULT_REVENUE);
        setTreasuryData(DEFAULT_TREASURY);
      }
    } catch {
      setRevenueData(DEFAULT_REVENUE);
      setTreasuryData(DEFAULT_TREASURY);
    }

    setActions(DEFAULT_ACTIONS);
    setDocuments(DEFAULT_DOCUMENTS);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (userId) fetchAll();
  }, [userId, fetchAll]);

  const refresh = useCallback(() => {
    if (userId) fetchAll();
  }, [userId, fetchAll]);

  return { kpis, revenueData, treasuryData, actions, documents, loading, refresh };
}
